import React, { useState } from "react";
import {
  Container,
  Box,
  Button,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Menu,
  MenuItem,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  Typography,
} from "@mui/material";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { Link, useNavigate } from "react-router-dom";
import { MoreVert } from "@mui/icons-material";
import { useGetApi } from "../hooks/useGetApi";

import ConfirmationDialog from "../components/confirmation-dialog/confirmation-dialog";

import {
  DEFAULT_LIMIT,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from "../utils/constants";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import dayjs from "dayjs";
import { getAllMedicineTrackingsApi } from "../services/medicine-tracking.service";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const headLabel = [
  {
    id: 1,
    label: "SNO",
    value: "sno",
  },
  {
    id: 2,
    label: "UHID",
    value: "uhid",
  },
  {
    id: 3,
    label: "Date",
    value: "date",
  },
  {
    id: 4,
    label: "Medicine Name",
    value: "medicineName",
  },
  {
    id: 5,
    label: "Ward",
    value: "ward",
  },
  {
    id: 6,
    label: "Bed Number",
    value: "bedNumber",
  },
  {
    id: 7,
    label: "Requested Quantity",
    value: "requestedQuantity",
  },
  {
    id: 8,
    label: "Delivered",
    value: "delivered",
  },
  {
    id: 9,
    label: "Status",
    value: "status",
  },
  {
    id: 10,
    label: "",
    value: "",
    align: "right",
  },
];

const popoverOptions = ["View Details"];

const buttons = [
  { label: "All", value: "All", width: "100px" },
  { label: "Pending", value: "Pending", width: "100px" },
  { label: "Approved", value: "Approved", width: "100px" },
  { label: "Delivered", value: "Delivered", width: "100px" },
];

const NursingAllMedicationTracking = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_LIMIT);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorPosition, setAnchorPosition] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [activeButton, setActiveButton] = useState(buttons?.[0]?.value);
  const [dateFilter, setDateFilter] = useState("today");

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const {
    dataList: allMedicineTrackingsList,
    dataCount: allMedicineTrackingsCount,
    isLoading,
    isError,
    refetch,
  } = useGetApi({
    apiFunction: getAllMedicineTrackingsApi,
    params: {
      limit: rowsPerPage,
      page: page + 1,
      status: activeButton,
      date: dateFilter,
    },
    dependencies: [page, rowsPerPage, activeButton, dateFilter],
    dataKey: "medicineTrackings",
    countKey: "total",
  });

  const handleRightClick = (event, row) => {
    event.preventDefault(); // Prevent the default context menu
    setAnchorEl(event.currentTarget);
    setAnchorPosition({
      top: event.clientY,
      left: event.clientX,
    });
    setSelectedRow(row); // Set the clicked row data
  };

  const handleClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setAnchorPosition(null);
    setSelectedRow(row); // Set the clicked row data
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleConfirmationModalOpen = () => {
    setConfirmationModalOpen(true);
  };

  const handleConfirmationModalClose = () => {
    setConfirmationModalOpen(false);
    setSelectedRow(null);
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleModalOpen = (index) => {
    handleClose();
  };
  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", pr: "90px", py: "20px" }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            color: '#5D6A82', 
            textTransform: 'none', 
            fontSize: '16px',
            fontWeight: 'bold',
            padding: 0,
            transition: 'none !important',
            mb: 2
          }}
        >
          Back
        </Button>
          {/* Filter */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: "20px",
            }}
          >
            <FormControl size="small" sx={{ minWidth: "150px" }}>
              <InputLabel id="date-select-label">Date</InputLabel>
              <Select
                labelId="date-select-label"
                label="Date"
                value={dateFilter || ""}
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <MenuItem value="all time">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="last week">Last Week</MenuItem>
                <MenuItem value="last month">Last Month</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {buttons?.map((button) => (
                <Button
                  key={button?.value}
                  variant="outlined"
                  onClick={() => {
                    setActiveButton(button?.value);
                  }}
                  sx={{
                    border: `2px solid ${
                      activeButton === button?.value
                        ? theme.palette.primary.dark
                        : "#c0c0c0"
                    }`,
                    color:
                      activeButton === button?.value
                        ? theme.palette.primary.dark
                        : "text.secondary",
                    bgcolor:
                      activeButton === button?.value ? "#AED5F3" : "#F0F7FC",
                    fontWeight: activeButton === button?.value ? 600 : 400,
                    width: button?.width || "100px",
                    textTransform: "none",
                  }}
                >
                  {button?.label || ""}
                </Button>
              ))}
            </Box>
          </Box>

          {/* table  */}
          {isLoading ? (
            <Loader />
          ) : isError ? (
            <ErrorMessage />
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table aria-label="nursing table" sx={{ minWidth: 800 }}>
                  <TableHead
                    sx={{
                      backgroundColor: "#f5f5f5",
                      "& th": { fontWeight: "bold" },
                    }}
                  >
                    <TableRow>
                      {headLabel?.map((option) => (
                        <TableCell
                          key={option?.id}
                          sx={{ textAlign: option?.align || "left" }}
                        >
                          {option?.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allMedicineTrackingsList?.map((row, index) => (
                      <TableRow
                        key={row?._id}
                        onContextMenu={(event) => handleRightClick(event, row)}
                      >
                        <TableCell>{row?.sNo || "-"}</TableCell>
                        <TableCell>{row?.uhid || "-"}</TableCell>
                        <TableCell>
                          {row?.date
                            ? dayjs(row?.date).format("YYYY-MM-DD")
                            : "-"}
                        </TableCell>
                        <TableCell>{row?.medicineName || "-"}</TableCell>
                        <TableCell>{row?.ward || "-"}</TableCell>
                        <TableCell>{row?.bedNumber || "-"}</TableCell>
                        <TableCell>{row?.requestedQuantity || "-"}</TableCell>
                        <TableCell>{row?.deliveredQuantity || "0"}</TableCell>
                        <TableCell>
                          <Typography
                            sx={{
                              color:
                                row?.status === "Pending"
                                  ? "#E00700"
                                  : row?.status === "Approved"
                                  ? "#FAAA07"
                                  : row?.status === "Delivered"
                                  ? "#61AD1A"
                                  : "black",
                            }}
                          >
                            {row?.status || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: "right" }}>
                          <MoreVert
                            sx={{ cursor: "pointer" }}
                            onClick={(event) => handleClick(event, row)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}

              <TablePagination
                rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                component="div"
                count={allMedicineTrackingsCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />

              {/* Pop-up Menu */}

              <Menu
                id={Boolean(anchorEl) ? "row-popover" : undefined}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorReference={anchorPosition ? "anchorPosition" : "anchorEl"}
                anchorPosition={
                  anchorPosition
                    ? { top: anchorPosition.top, left: anchorPosition.left }
                    : undefined
                }
                anchorOrigin={{
                  vertical: "center",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                slotProps={{
                  paper: {
                    sx: {
                      borderRadius: "8px",
                      boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                      maxHeight: "300px",
                    },
                  },
                }}
              >
                {popoverOptions?.map((option, index) => (
                  <MenuItem
                    key={option || index}
                    onClick={() => handleModalOpen(index)}
                  >
                    {option || ""}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}

          {/* confirmation modal */}
          <ConfirmationDialog
            open={confirmationModalOpen}
            onCancel={handleConfirmationModalClose}
            // onConfirm={bedStatusUpdate}
          />
        </Box>
      </Box>
    </Container>
  );
};

export default NursingAllMedicationTracking;
